import React from "react";
import { motion } from "framer-motion";
import { Check, Loader2, MessageCircle, PartyPopper, Send, Clock, Bot } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

export const FunnelLoading = () => (
  <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-12">
    <Loader2 className="animate-spin text-purple-500 mx-auto mb-4" size={40} />
    <p className="font-display font-bold text-xl">Analizando tus respuestas...</p>
    <p className="text-sm text-zinc-500 mt-2">Nuestra IA está revisando tu perfil ✨</p>
  </motion.div>
);

export const FunnelResult = ({ result }) => (
  <motion.div key="result" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="rounded-2xl border border-white/10 bg-[#0d0d0d] p-8 text-center glow-purple">
    {result.qualified ? (
      <>
        <div className="w-16 h-16 rounded-full bg-yellow-400/20 flex items-center justify-center mx-auto mb-4">
          <PartyPopper className="text-yellow-400" size={32} />
        </div>
        <h3 className="font-display font-black text-2xl">{result.message}</h3>
        <p className="text-zinc-400 mt-3 mb-6">Escríbenos ahora por WhatsApp y platiquemos sobre tu futuro. 👇</p>
        <a data-testid="result-whatsapp" href={result.whatsapp_invite} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 w-full bg-green-600 hover:bg-green-500 text-white font-bold rounded-xl py-5 text-base">
          <MessageCircle size={20} /> Hablar por WhatsApp ahora
        </a>
      </>
    ) : (
      <>
        <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
          <Check className="text-green-400" size={32} />
        </div>
        <h3 className="font-display font-bold text-2xl">{result.message}</h3>
      </>
    )}
  </motion.div>
);

export const FunnelChat = ({ chat, chatInput, setChatInput, sending, sendChat }) => (
  <motion.div key="chat" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl border border-white/10 bg-[#0d0d0d] flex flex-col h-[78vh] max-h-[640px] overflow-hidden glow-purple">
    <div className="flex items-center justify-between gap-2 px-5 py-3.5 border-b border-white/10 bg-[#121212]">
      <div className="flex items-center gap-2">
        <span className="w-9 h-9 rounded-full bg-purple-600 flex items-center justify-center"><Bot size={18} /></span>
        <div>
          <p className="font-display font-bold text-sm leading-tight">Asesor MULTICONTACTOS</p>
          <p className="text-[11px] text-green-400 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" /> en línea</p>
        </div>
      </div>
      {!chat.ended && (
        <span data-testid="chat-timer" className="flex items-center gap-1.5 text-xs text-zinc-400 bg-black/40 rounded-full px-3 py-1">
          <Clock size={13} /> {Math.floor(chat.secondsLeft / 60)}:{String(chat.secondsLeft % 60).padStart(2, "0")}
        </span>
      )}
    </div>

    <div data-testid="chat-messages" className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
      {chat.messages.map((m, i) => (
        <div key={`m-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
          <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-yellow-400 text-black rounded-br-sm" : "bg-[#1c1c1c] text-zinc-100 rounded-bl-sm border border-white/5"}`}>
            {m.content}
          </div>
        </div>
      ))}
      {sending && (
        <div className="flex justify-start">
          <div className="bg-[#1c1c1c] border border-white/5 rounded-2xl rounded-bl-sm px-4 py-3">
            <Loader2 className="animate-spin text-purple-400" size={16} />
          </div>
        </div>
      )}
      {chat.ended && chat.qualified && chat.whatsapp && (
        <a data-testid="chat-whatsapp" href={chat.whatsapp} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center justify-center gap-2 w-full bg-green-600 hover:bg-green-500 text-white font-bold rounded-xl py-4">
          <MessageCircle size={20} /> Hablar por WhatsApp ahora
        </a>
      )}
      {chat.ended && (!chat.qualified || !chat.whatsapp) && (
        <p data-testid="chat-ended" className="text-center text-xs text-zinc-500 pt-2">La conversación ha finalizado. ¡Gracias! 🙌</p>
      )}
    </div>

    {!chat.ended && (
      <div className="flex items-center gap-2 px-3 py-3 border-t border-white/10 bg-[#121212]">
        <Input
          data-testid="chat-input"
          value={chatInput}
          onChange={(e) => setChatInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") sendChat(); }}
          placeholder="Escribe tu mensaje..."
          disabled={sending}
          className="bg-black border-white/15 rounded-full py-5 focus-visible:ring-purple-500"
        />
        <Button data-testid="chat-send" onClick={sendChat} disabled={sending || !chatInput.trim()} className="shrink-0 rounded-full w-11 h-11 p-0 bg-yellow-400 hover:bg-yellow-300 text-black">
          <Send size={18} />
        </Button>
      </div>
    )}
  </motion.div>
);
